import { useState, useEffect } from 'react';
import { IconType } from 'react-icons';
import { useRealtimeChatContext } from 'src/fusion-copilot/hooks/useRealtimeChatContext';

export type SidebarPanel = 'copilot' | 'support' | null;

export interface PanelConfig {
	id: string;
	icon: string | IconType;
	title: string;
	color?: string;
	content: React.ComponentType;
	order?: number;
}

export const useRightSidebar = () => {
	// Restore sidebar state from sessionStorage on mount
	const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
		try {
			const savedState = sessionStorage.getItem('rightSidebarOpen');
			return savedState === 'true';
		} catch {
			return false;
		}
	});

	const [activePanel, setActivePanel] = useState<SidebarPanel>(() => {
		try {
			const savedPanel = sessionStorage.getItem('rightSidebarActivePanel');
			return (savedPanel as SidebarPanel) || 'copilot';
		} catch {
			return 'copilot';
		}
	});

	const { closeCopilot } = useRealtimeChatContext();

	// Save sidebar state to sessionStorage whenever it changes
	useEffect(() => {
		try {
			sessionStorage.setItem('rightSidebarOpen', isSidebarOpen.toString());
		} catch (error) {
			console.error('Failed to save sidebar state:', error);
		}
	}, [isSidebarOpen]);

	useEffect(() => {
		try {
			sessionStorage.setItem('rightSidebarActivePanel', activePanel || '');
		} catch (error) {
			console.error('Failed to save active panel:', error);
		}
	}, [activePanel]);

	const handleOpenSidebar = (panel: SidebarPanel) => {
		setActivePanel(panel);
		setIsSidebarOpen(true);
	};

	const handlePanelToggle = (panel: SidebarPanel) => {
		if (activePanel === panel) {
			setActivePanel(null);
			setIsSidebarOpen(false);
		} else {
			setActivePanel(panel);
			setIsSidebarOpen(true);
		}
	};

	const handleCloseSidebar = () => {
		setIsSidebarOpen(false);
		setActivePanel(null);
	};

	return {
		isSidebarOpen,
		activePanel,
		handleOpenSidebar,
		handlePanelToggle,
		handleCloseSidebar,
	};
};
